import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  Avatar,
  createAvatarProps,
  List,
  SegmentedControl,
} from '@usepepper/sprout-kit-ui';
import { InsightCard } from '@/experiences/sales-portal/domain/insights/components/insight-card';
import { LowestPerformingCustomersCardMode } from '@/enums';
import { LowestPerformingCustomersHubCardData } from '@/experiences/sales-portal/domain/insights/blocs/types/insight';

export interface LowestPerformingCustomersCardProps {
  data: LowestPerformingCustomersHubCardData;
  onCustomerPress: (restaurantUUID: string) => void;
}

export const LowestPerformingCustomersCard: React.FC<
  LowestPerformingCustomersCardProps
> = ({ data, onCustomerPress }) => {
  const displayModeOptions = [
    {
      label: i18n.t(
        'insights_hub.lowest_performing_customers.toggle.gp_percentage',
      ),
      value: LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE,
    },
    {
      label: i18n.t(
        'insights_hub.lowest_performing_customers.toggle.units_sold',
      ),
      value: LowestPerformingCustomersCardMode.UNITS_SOLD,
    },
  ];

  const [mode, setMode] = useState<LowestPerformingCustomersCardMode>(
    LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE,
  );
  const handleModeChange = (value: string) => {
    if (
      value === LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE ||
      value === LowestPerformingCustomersCardMode.UNITS_SOLD
    ) {
      setMode(value);
    }
  };

  const showToggle = data.byGpPercentage.length > 0;
  const showGrossProfit =
    showToggle && mode === LowestPerformingCustomersCardMode.AVG_GP_PERCENTAGE;

  const customers = showGrossProfit ? data.byGpPercentage : data.byUnitsSold;

  return (
    <InsightCard
      actionsElement={
        showToggle ? (
          <SegmentedControl
            aria-label={data.title}
            items={displayModeOptions}
            onValueChange={handleModeChange}
            size="$sm"
            value={mode}
          />
        ) : undefined
      }
      subtitle={i18n.t(
        showGrossProfit
          ? 'insights_hub.lowest_performing_customers.gp_percentage'
          : 'insights_hub.lowest_performing_customers.units_sold',
      )}
      title={data.title}
    >
      <List>
        {customers.map((row) => (
          <List.Item
            key={row.restaurantUUID}
            onPress={() => onCustomerPress(row.restaurantUUID)}
            paddingHorizontal={0}
            pressable
            size="$sm"
          >
            <List.Item.Leading>
              <Avatar
                {...createAvatarProps(
                  row.restaurantName,
                  row.restaurantUUID,
                  1,
                )}
                size="$sm"
              />
            </List.Item.Leading>
            {/* TODO: delete once SKUI reserves the gap on List.Item.Value. */}
            <List.Item.Content alignSelf="flex-start" marginRight="$12">
              <List.Item.Title numberOfLines={1}>
                {row.restaurantName}
              </List.Item.Title>
              <List.Item.Subtitle numberOfLines={1}>
                {row.subtitle}
              </List.Item.Subtitle>
            </List.Item.Content>
            {/* TODO: drop once SKUI stops letting List.Item.Value shrink. */}
            <List.Item.Value
              alignSelf="flex-start"
              flexShrink={0}
              numberOfLines={1}
            >
              {row.formattedValue}
            </List.Item.Value>
          </List.Item>
        ))}
      </List>
    </InsightCard>
  );
};
